import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  Container,
  Group,
  PasswordInput,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck } from '@tabler/icons-react';
import { handleSubmitError } from '@utils/forms';

import { useUpdateCustomerMutation } from '@api/customers.api';
import PasswordInputWithRequirements from './PasswordInputWithRequirements';

export default function EditPassword() {
  // ==========================================================================
  // State
  // ==========================================================================
  const [isSuccess, setIsSuccess] = useState(false);
  const { t } = useTranslation();

  // ==========================================================================
  // Api
  // ==========================================================================
  const [updateCustomer, { isLoading: isUpdating }] =
    useUpdateCustomerMutation();

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    oldPassword: '',
    password: '',
    passwordConfirm: '',
  };

  const form = useForm({
    initialValues,
    validate: {
      passwordConfirm: (value, values) =>
        value !== values.password
          ? t('editPassword.validation.passwordMismatch')
          : null,
    },
  });

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const onSubmit = async (values: typeof initialValues) => {
    try {
      await updateCustomer({
        password: values.password,
        oldPassword: values.oldPassword,
      }).unwrap();

      form.reset();

      setIsSuccess(true);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Container mt={{ base: 'md', xs: 0 }}>
      <Group justify="space-between">
        <Title mb="md" order={2}>
          {t('editPassword.title')}
        </Title>
      </Group>

      <form
        onSubmit={form.onSubmit((values) => {
          onSubmit(values);
        })}
      >
        <Stack maw="30rem">
          <PasswordInput
            label={t('editPassword.oldPassword.label')}
            placeholder={t('editPassword.oldPassword.placeholder')}
            required
            data-autofocus
            {...form.getInputProps('oldPassword')}
          />
          <PasswordInputWithRequirements
            label={t('editPassword.newPassword.label')}
            placeholder={t('editPassword.newPassword.placeholder')}
            required
            {...form.getInputProps('password')}
          />
          <PasswordInput
            label={t('editPassword.passwordConfirm.label')}
            placeholder={t('editPassword.passwordConfirm.placeholder')}
            required
            {...form.getInputProps('passwordConfirm')}
          />
        </Stack>

        {form.errors.general && (
          <Text c="red" size="sm" mt="xl">
            {form.errors.general}
          </Text>
        )}

        {isSuccess && (
          <Alert color="green" mt="xl" icon={<IconCheck />}>
            {t('editPassword.successMessage')}
          </Alert>
        )}

        <Button mt="xl" type="submit" loading={isUpdating}>
          {t('editPassword.submit')}
        </Button>
      </form>
    </Container>
  );
}
